import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Icon,
  Skeleton,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PlusIcon } from '@heroicons/react/outline';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { getBrand, getHostName, isFeatureEnabled, routes } from '@blockpulse3/data/shared';
import { UserRole, useGetOptionAssetsQuery } from '@blockpulse3/graphql/hooks';
import { ErrorQueryCard } from '@blockpulse3/ui/commons';
import { useUser } from '@blockpulse3/web-client/auth';

import { OptionCreateModal } from '../OptionCreateModal';
import { OptionTableCurrent } from './OptionTable';

type Props = unknown;

export function OptionList(): JSX.Element | null {
  const i18nOptionList = useTranslations('OptionList');

  const { isOpen, onOpen, onClose } = useDisclosure();

  const { user } = useUser();
  const navigate = useNavigate();
  const { companyId = '' } = useParams();
  const isFeatureOptionPool = isFeatureEnabled(getBrand(getHostName()), 'optionPool');

  const { data, loading, error, refetch } = useGetOptionAssetsQuery({
    variables: { companyId: companyId || '' },
    skip: !companyId && !isFeatureOptionPool,
  });

  const handleOptionNavigation = (assetId: string): void => {
    if (companyId) {
      navigate(routes.workspace.option.fmt(companyId, assetId));
    }
  };

  if (loading) {
    return (
      <Skeleton>
        <Card height="330px" />
      </Skeleton>
    );
  }

  if (error) {
    return <ErrorQueryCard h="300px" />;
  }

  const currentOptions = data?.getOptionAssets || [];

  if (!isFeatureOptionPool || !currentOptions?.length) return null;

  const handleOpenModal = (): void => {
    onOpen();
  };

  const handleCreateOptionComplete = (): void => {
    refetch();
  };

  const isSuperAdmin = user?.roles.includes(UserRole.SUPERADMIN);

  return (
    <>
      <Card>
        <CardHeader
          as={Stack}
          direction={{ base: 'column', md: 'row' }}
          alignItems={{ base: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Heading size="md">{i18nOptionList('MyOptions')}</Heading>
          {isSuperAdmin && (
            <Button
              leftIcon={<Icon as={PlusIcon} boxSize="18px" />}
              onClick={handleOpenModal}
              variant="secondary"
            >
              {i18nOptionList('AddOption')}
            </Button>
          )}
        </CardHeader>
        <CardBody>
          <Tabs defaultIndex={0} isLazy>
            <TabList>
              <Tab>
                <Text>{i18nOptionList('InProgress')}</Text>
                {/*<Badge ml="2">{started}</Badge>*/}
              </Tab>
              <Tab isDisabled>
                <Text>{i18nOptionList('Past')}</Text>
                {/*<Badge ml="2">{passed}</Badge>*/}
              </Tab>
              <Tab isDisabled>
                <Text>{i18nOptionList('Draft')}</Text>
                {/*<Badge ml="2">{drafts}</Badge>*/}
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel px="0">
                <OptionTableCurrent onClick={handleOptionNavigation} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>
      <OptionCreateModal
        isOpen={isOpen}
        onClose={onClose}
        onComplete={handleCreateOptionComplete}
      />
    </>
  );
}

export type OptionListProps = Props;
